import * as productApi from '@apis/app-products';
import * as userApi from '@apis/app-user';
import BottomRoundButton from '@components/common/BottomRoundButton';
import { PaymentMethod, PAYMENT_METHODS_DICT } from '@consts/payment';
import { usePayment } from '@hooks/payment';
import { ClassInvoice } from '@models/invoice';
import { CreatePaymentStatementsBody } from '@models/paymentStatement';
import { useQuery } from '@tanstack/react-query';
import { getUniqueKeyFromUUID } from '@utils/string';
import { Divider } from 'antd';
import moment from 'moment';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { useEffect, useState } from 'react';
import ClassAddressView from '../ClassAddressView';
import ClassFeeView from '../ClassFeeView';
import { ClassInvoiceSummary } from '../ClassInvoiceSummary';
import './ClassInvoice.scss';
import PaymentInProgress from './PaymentInProgress';
import PointUse from './PointUse';
import SelectPaymentMethod from './SelectPaymentMethod';

interface ClassInvoicePaymentProps {
  classInvoice: ClassInvoice;
  isTutorLogin?: boolean;
  onCancel?: () => void;
  onPaymentCompleted?: () => void;
}

export const ClassInvoicePayment: React.FC<ClassInvoicePaymentProps> = (props) => {
  const payment = usePayment();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(PAYMENT_METHODS_DICT.card);
  const [usedCreditPoint, setUsedCreditPoint] = useState<number>(0);
  const { classInvoice } = props;
  const selectedChildrenList = classInvoice?.students || [];
  const otherChildrenCount = classInvoice?.additionalParticipantCount || 0;
  const classThumbnail = classInvoice.classThumbnail;
  const parentId = classInvoice.parentId;
  const parentUserQuery = useQuery({
    queryKey: [`user-${parentId}`],
    queryFn: () => userApi.getUser(parentId),
    enabled: !!parentId,
  });

  const classId = classInvoice.classId;
  const classItemQuery = useQuery({
    queryKey: [`class-item-${classId}`],
    queryFn: () => productApi.getProduct(classId!),
    enabled: !!classId,
  });

  const classProduct = classItemQuery.data?.data;
  const parentUser = parentUserQuery.data?.data;
  const userPoint = parentUser?.profile?.creditPoints || 0;

  useEffect(() => {
    if (payment.status === 'done') {
      props.onPaymentCompleted && props.onPaymentCompleted();
    }
  }, [payment.status]);

  let chidrenText = `${selectedChildrenList
    .map((child) => {
      return child.name;
    })
    .join(', ')}`;

  if (otherChildrenCount > 0) {
    chidrenText = `${selectedChildrenList
      .map((child) => {
        return child.name;
      })
      .join(', ')} 외 ${otherChildrenCount}명`;
  }

  const onClickPayment = () => {
    const purchases = classInvoice.schedules.map((schedule) => {
      return {
        name: moment(schedule.startDate).format('YYYY-MM-DD'),
        fee: classProduct?.fee || 0,
        count: selectedChildrenList.length + otherChildrenCount,
      };
    });

    if (classInvoice.applyGroupDiscount) {
      purchases.push({ name: 'discount', fee: classInvoice.totalAmount - classInvoice.discountedAmount, count: 1 });
    }

    const paymentStatement: CreatePaymentStatementsBody = {
      uuid: getUniqueKeyFromUUID(),
      seller: { id: classInvoice.tutorId, name: classInvoice.tutorName },
      buyer: {
        id: parentId!,
        name: parentUser?.profile?.name,
        phone: parentUser?.profile?.phone,
      },
      product: {
        saleType: 'class',
        id: classInvoice.classId,
        name: `${classInvoice.classTitle} ${chidrenText}`,
        classInvoiceId: classInvoice._id,
        applyGroupDiscount: classInvoice.applyGroupDiscount,
      },
      purchases: purchases,
      payment: {
        method: paymentMethod.type,
        gateway: paymentMethod.paymentGateway,
        amount: classInvoice.discountedAmount,
        point: usedCreditPoint,
        billingAmount: classInvoice.discountedAmount - usedCreditPoint,
      },
    };

    payment.requestPayment(paymentStatement);
  };

  if (payment.status === 'in_progress') {
    return <PaymentInProgress />;
  }

  const classFeePerTime = classInvoice.classFeePerTime || classInvoice.discountedAmount / 4 || classProduct?.fee;

  return (
    <OverlayScrollbarsComponent
      defer
      options={{
        scrollbars: { autoHide: 'scroll' },
        overflow: {
          x: 'hidden',
        },
      }}
      style={{
        backgroundColor: '#ffffff',
        marginTop: 0,
        position: 'relative',
        height: '100%',
        overflow: 'auto',
        width: '100%',
      }}>
      <div style={{ width: '100%' }}>
        <div style={{ padding: '20px 16px', backgroundColor: '#EFF1F4' }}>
          <ClassInvoiceSummary
            payment
            tutorProfileImage={classInvoice.tutorProfileImage}
            tutorName={classInvoice.tutorName}
            classTitle={classInvoice.classTitle}
            classThumbnail={classThumbnail}
            students={selectedChildrenList}
            additionalParticipantCount={otherChildrenCount}
            totalClassTimes={classInvoice.totalClassTimes}
            classDuration={classInvoice.classDuration}
            schedules={classInvoice.schedules}
          />
        </div>
        <ClassAddressView address={classInvoice.address} addressDetail={classInvoice.addressDetail} />
        <Divider />
        <PointUse
          userPoint={userPoint}
          totalFee={classInvoice.discountedAmount}
          productShippingFee={0}
          onChangeApplyPoint={(value) => {
            setUsedCreditPoint(value);
          }}
        />
        <Divider />
        <SelectPaymentMethod
          onChange={(value) => {
            setPaymentMethod(value);
          }}
        />
        <Divider />

        <ClassFeeView
          defaultClassFee={classFeePerTime}
          totalClassTimes={classInvoice.totalClassTimes}
          totalStudentCount={selectedChildrenList.length + otherChildrenCount}
          applyGroupDiscount={classInvoice.applyGroupDiscount}
          usedPoint={usedCreditPoint || 0}
        />
        <div style={{ padding: '24px 20px', backgroundColor: '#F5F7FB' }}>
          <div
            style={{
              color: 'var(--Neutral-70, #808387)',
              textAlign: 'center',
              fontFamily: 'Pretendard',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '20px',
            }}>
            위 내용을 확인하였으며 결제에 동의합니다.
          </div>
          <BottomRoundButton
            onClick={() => {
              onClickPayment();
            }}
            style={{ marginTop: '40px' }}>
            결제하기
          </BottomRoundButton>
        </div>
      </div>
    </OverlayScrollbarsComponent>
  );
};
